import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataProvider, useGetList, useNotify, Title } from 'react-admin';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import type { Client, Configuration, FieldError, Template } from '../../types';
import type { LogtoDataProvider } from '../../dataProvider';
import { SplashScreenInput } from './SplashScreenInput';
import { TemplateSettingsForm, apiErrorMessage, validateSettings, withDefaults } from '../templateSettings';

export const ConfigurationEdit = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'icon' | 'adaptive' | 'adaptiveBg' | null>(null);
  const [config, setConfig] = useState<Configuration | null>(null);

  // Form fields matching specification
  const [appName, setAppName] = useState('');
  const [bundleIdAndroid, setBundleIdAndroid] = useState('');
  const [bundleIdIos, setBundleIdIos] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [adaptiveIconUrl, setAdaptiveIconUrl] = useState('');
  const [adaptiveIconBackground, setAdaptiveIconBackground] = useState('#FFFFFF');
  const [adaptiveIconBackgroundImageUrl, setAdaptiveIconBackgroundImageUrl] = useState('');
  const [splashScreenUrl, setSplashScreenUrl] = useState<string | null>(null);
  const [features, setFeatures] = useState<Record<string, any>>({});
  // Feature 012: the client's template drives the extra settings section.
  const [client, setClient] = useState<Client | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  // The selected template becomes the client's template on save.
  const { data: templates } = useGetList<Template>('templates', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'key', order: 'ASC' },
  });
  const [shownKey, setShownKey] = useState<string>('');
  const shownTemplate = templates?.find((t) => t.key === shownKey) || template;
  const templateChanged = !!template && !!shownTemplate && shownTemplate.key !== template.key;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!clientId) return;

    Promise.all([
      dataProvider.getOne('configurations', { id: clientId }),
      dataProvider
        .getOne<Client>('clients', { id: clientId })
        .then(({ data: c }) => {
          setClient(c);
          return dataProvider.getOne<Template>('templates', { id: c.templateKey || 'v1' });
        }),
    ])
      .then(([{ data }, { data: tpl }]) => {
        setTemplate(tpl);
        setConfig(data as Configuration);
        setAppName(data.appName || '');
        setBundleIdAndroid(data.bundleIdAndroid || '');
        setBundleIdIos(data.bundleIdIos || '');
        setIconUrl(data.iconUrl || '');
        setAdaptiveIconUrl(data.adaptiveIconUrl || '');
        setAdaptiveIconBackground(data.adaptiveIconBackground || '#FFFFFF');
        setAdaptiveIconBackgroundImageUrl(data.adaptiveIconBackgroundImageUrl || '');
        setSplashScreenUrl(data.splashScreenUrl || null);
        setFeatures(withDefaults(tpl.settingsSchema, data.features || {}));
      })
      .catch(() => {
        notify('Failed to load configuration', { type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, dataProvider, notify]);

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'icon' | 'adaptive' | 'adaptiveBg') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify('Please select an image file', { type: 'error' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify('File size must be less than 5MB', { type: 'error' });
      return;
    }

    setUploading(type);
    try {
      const result = await dataProvider.uploadImage(file);
      if (type === 'icon') {
        setIconUrl(result.url);
      } else if (type === 'adaptive') {
        setAdaptiveIconUrl(result.url);
      } else {
        setAdaptiveIconBackgroundImageUrl(result.url);
      }
      const label = type === 'icon' ? 'Icon' : type === 'adaptive' ? 'Adaptive icon' : 'Adaptive icon background';
      notify(`${label} uploaded successfully`, { type: 'success' });
    } catch (error) {
      notify('Failed to upload image', { type: 'error' });
    } finally {
      setUploading(null);
    }
  };

  const handleSplashScreenUpload = async (file: File): Promise<string> => {
    if (!clientId) {
      throw new Error('Client ID is required');
    }

    const result = await dataProvider.uploadSplashScreen(clientId, file);
    notify('Splash screen uploaded successfully (new version created)', { type: 'success' });

    // Update config state with new version
    if (config) {
      setConfig({ ...config, version: result.version, splashScreenUrl: result.splashScreenUrl });
    }

    return result.splashScreenUrl;
  };

  const handleSplashScreenRemove = async (): Promise<void> => {
    if (!clientId) {
      throw new Error('Client ID is required');
    }

    const result = await dataProvider.removeSplashScreen(clientId);
    notify('Splash screen removed successfully (new version created)', { type: 'success' });

    // Update config state with new version
    if (config) {
      setConfig({ ...config, version: result.version, splashScreenUrl: undefined });
    }
  };

  const handleSave = async () => {
    if (!clientId || !config) return;

    // Validate template settings (the API checks the same rules)
    if (shownTemplate) {
      const errors = validateSettings(shownTemplate.settingsSchema, features);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        notify('Проверьте настройки шаблона', { type: 'error' });
        return;
      }
    }

    setSaving(true);
    // The API validates the configuration against the client's template, so
    // switch the template first and switch it back if the save fails.
    const switchTemplate = (templateKey: string) =>
      client &&
      dataProvider.update<Client>('clients', {
        id: clientId,
        data: {
          name: client.name,
          status: client.status,
          zenkyStoreId: client.zenkyStoreId || undefined,
          useSharedIosAccount: client.useSharedIosAccount,
          useSharedAndroidAccount: client.useSharedAndroidAccount,
          templateKey,
        },
        previousData: client,
      });
    let switched = false;
    try {
      if (templateChanged && shownTemplate) {
        try {
          await switchTemplate(shownTemplate.key);
          switched = true;
        } catch (error) {
          notify(apiErrorMessage(error, 'Не удалось сменить шаблон клиента'), { type: 'error' });
          return;
        }
      }
      await dataProvider.update('configurations', {
        id: clientId,
        data: {
          clientId,
          appName,
          bundleIdAndroid,
          bundleIdIos,
          iconUrl,
          adaptiveIconUrl,
          adaptiveIconBackground,
          adaptiveIconBackgroundImageUrl,
          splashScreenUrl: splashScreenUrl || undefined,
          features,
        },
        previousData: config,
      });
      notify(
        switched
          ? `Конфигурация сохранена, шаблон клиента: ${shownTemplate?.key}`
          : 'Configuration saved successfully (new version created)',
        { type: 'success' }
      );
      navigate(`/clients/${clientId}/show`);
    } catch (error: any) {
      if (switched && template) {
        await switchTemplate(template.key)?.catch(() =>
          notify(`Шаблон клиента остался ${shownTemplate?.key}, конфигурация не сохранена`, { type: 'warning' })
        );
      }
      const fields: FieldError[] | undefined = error?.body?.fields;
      if (fields?.length) {
        setFieldErrors(Object.fromEntries(fields.map((f) => [f.field, f.message])));
        notify('Проверьте настройки шаблона', { type: 'error' });
        return;
      }
      notify('Failed to save configuration', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box p={4}>
        <Alert severity="error">Configuration not found</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Title title={`Edit Configuration - ${config.appName}`} />
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Edit Configuration
            </Typography>
            <Chip
              label={`Version ${config.version}`}
              color="primary"
              size="small"
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Saving will create a new version (v{config.version + 1}). Previous versions are kept for history.
          </Alert>

          <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
            {/* App Name */}
            <TextField
              label="App Name"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              fullWidth
              margin="normal"
              required
              helperText="Display name for the application"
            />

            {/* Bundle IDs */}
            <TextField
              label="Android Bundle ID"
              value={bundleIdAndroid}
              onChange={(e) => setBundleIdAndroid(e.target.value)}
              fullWidth
              margin="normal"
              required
              helperText="Reverse domain notation (e.g., com.example.app)"
            />

            <TextField
              label="iOS Bundle ID"
              value={bundleIdIos}
              onChange={(e) => setBundleIdIos(e.target.value)}
              fullWidth
              margin="normal"
              required
              helperText="Reverse domain notation (e.g., com.example.app)"
            />

            {/* Icon Upload */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                App Icon (1024x1024)
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {iconUrl && (
                  <Avatar
                    src={iconUrl}
                    variant="rounded"
                    sx={{ width: 80, height: 80 }}
                  />
                )}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="icon-upload"
                    type="file"
                    onChange={(e) => handleIconUpload(e, 'icon')}
                    disabled={uploading === 'icon'}
                  />
                  <label htmlFor="icon-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploading === 'icon' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      disabled={uploading === 'icon'}
                    >
                      {uploading === 'icon' ? 'Uploading...' : 'Upload Icon'}
                    </Button>
                  </label>
                  {iconUrl && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {iconUrl}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Adaptive Icon Upload */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Adaptive Icon (Android)
              </Typography>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    backgroundColor: adaptiveIconBackgroundImageUrl ? 'transparent' : adaptiveIconBackground,
                    backgroundImage: adaptiveIconBackgroundImageUrl ? `url(${adaptiveIconBackgroundImageUrl})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ccc',
                    flexShrink: 0,
                  }}
                >
                  {adaptiveIconUrl && (
                    <img
                      src={adaptiveIconUrl}
                      alt="Adaptive Icon"
                      style={{ width: 56, height: 56, objectFit: 'contain' }}
                    />
                  )}
                </Box>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="adaptive-icon-upload"
                    type="file"
                    onChange={(e) => handleIconUpload(e, 'adaptive')}
                    disabled={uploading === 'adaptive'}
                  />
                  <label htmlFor="adaptive-icon-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploading === 'adaptive' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      disabled={uploading === 'adaptive'}
                    >
                      {uploading === 'adaptive' ? 'Uploading...' : 'Upload Adaptive Icon'}
                    </Button>
                  </label>
                  <TextField
                    label="Background Color"
                    value={adaptiveIconBackground}
                    onChange={(e) => setAdaptiveIconBackground(e.target.value)}
                    disabled={saving || !!adaptiveIconBackgroundImageUrl}
                    size="small"
                    sx={{ mt: 1, width: 200, display: 'block' }}
                    helperText={adaptiveIconBackgroundImageUrl ? 'Ignored while background image is set' : undefined}
                  />
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      accept="image/png"
                      style={{ display: 'none' }}
                      id="adaptive-bg-upload"
                      type="file"
                      onChange={(e) => handleIconUpload(e, 'adaptiveBg')}
                      disabled={uploading === 'adaptiveBg'}
                    />
                    <label htmlFor="adaptive-bg-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={uploading === 'adaptiveBg' ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={uploading === 'adaptiveBg'}
                      >
                        {uploading === 'adaptiveBg' ? 'Uploading...' : 'Upload Background Image'}
                      </Button>
                    </label>
                    {adaptiveIconBackgroundImageUrl && (
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={() => setAdaptiveIconBackgroundImageUrl('')}
                        disabled={saving}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  {adaptiveIconBackgroundImageUrl && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                      {adaptiveIconBackgroundImageUrl}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Splash Screen Upload */}
            <SplashScreenInput
              value={splashScreenUrl || undefined}
              onChange={(url) => setSplashScreenUrl(url)}
              onUpload={handleSplashScreenUpload}
              onRemove={handleSplashScreenRemove}
              disabled={saving}
            />

            {/* Template settings (feature 012) */}
            <Divider sx={{ my: 3 }} />
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Typography variant="h6">Настройки шаблона</Typography>
              {template && (
                <TextField
                  select
                  size="small"
                  value={shownTemplate?.key || ''}
                  onChange={(e) => {
                    const next = templates?.find((t) => t.key === e.target.value);
                    setShownKey(e.target.value);
                    if (next) setFeatures((prev) => withDefaults(next.settingsSchema, prev));
                  }}
                  sx={{ minWidth: 260 }}
                >
                  {(templates || [template]).map((t) => (
                    <MenuItem key={t.key} value={t.key}>
                      {t.name} ({t.key}){t.key === template.key ? ' — текущий' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            {templateChanged && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                После сохранения клиент будет собираться из шаблона {shownTemplate?.key} (сейчас{' '}
                {template?.key}). Для тестовой сборки без переключения выберите шаблон в форме сборки.
              </Alert>
            )}
            {shownTemplate && clientId && (
              <TemplateSettingsForm
                key={shownTemplate.key}
                clientId={clientId}
                template={shownTemplate}
                features={features}
                onChange={(next) => {
                  setFeatures(next);
                  setFieldErrors({});
                }}
                errors={fieldErrors}
                disabled={saving}
              />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Настройки других шаблонов сохраняются и применятся, если клиента переключить обратно.
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || uploading !== null}
              >
                {saving ? 'Saving...' : 'Save (Create New Version)'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(`/clients/${clientId}/show`)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
